import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AppInfoService } from 'core/app-info/app-info.service';
import { UtilsService } from 'core/utils/utils.service';
import { MatPaginator } from '@angular/material/paginator';
import { PaginationPageEvent } from '@snapon/design-system-angular';
import { AppLocalization } from 'src/app/modules/shared/entities/app-localization';
import { WarehouseProduct } from '../entities/consigned-warehouse.entity';
import { ConsignedWarehouseService } from '../services/consigned-warehouse.service';
import { QuoteDetailsService } from '../services/quote-details.service';
import { QuoteToolsService } from '../services/quote-tools.service';

@Component({
  selector: 'app-consigned-warehouse',
  templateUrl: './consigned-warehouse.component.html',
  styleUrls: ['./consigned-warehouse.component.scss'],
  standalone: false
})
export class ConsignedWarehouseComponent implements OnInit {
  appStrings: AppLocalization;
  currencySymbol: string;
  showConfirmationOverlay = false;
  warehouseList: { value: string, label: string }[];
  hasItemsAdded: boolean;

  displayedColumns = [
    'lineNumber',
    'partNumber',
    'description',
    'lastReceivedDate',
    'discontinued',
    'availableQuantity',
    'addQuantity'
  ];

  oldSelectedWarehouse;
  selectedWarehouse;
  warehouseForm: FormGroup;
  recordList = [];
  quoteDetails;
  quantities = {};
  quantityMap = new Map();
  isLoading: boolean;
  displayDataSource: any[] = [];
  currentPage = 0;
  pageSize = 10;
  pageOffset = 0;
  totalPages = 0;


  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private appInfoService: AppInfoService,
    private consignedWarehouseService: ConsignedWarehouseService,
    public dialogRef: MatDialogRef<ConsignedWarehouseComponent>,
    private quoteDetailsService: QuoteDetailsService,
    private quoteToolsService: QuoteToolsService,
    private utilsService: UtilsService,
  ) {
    this.warehouseForm = new FormGroup({
      selectedWarehouse: new FormControl('')
    });
  }

  @ViewChild(MatPaginator) paginator: MatPaginator;

  ngOnInit() {
    this.appStrings = this.appInfoService.appStrings;
    this.currencySymbol = this.appInfoService.currencySymbol;
    this.quoteDetails = this.quoteDetailsService.summary.quoteDetails;
    this.isLoading = false;
    this.initData();
  }

  /**
   * get warehouse list
   * infrastructure for updating the values
   */
  initData() {
    this.warehouseList = this.utilsService.mapObjectToSelectMultipleLabel(
      this.consignedWarehouseService.whList, 'warehouseCode', ['warehouseCode', 'warehouseName']);
    this.selectedWarehouse = this.warehouseList[0];
    this.oldSelectedWarehouse = this.warehouseList[0];
    this.warehouseForm.controls.selectedWarehouse.setValue(this.selectedWarehouse.value);
    this.updateProductList();
  }

  private updateProductList() {
    const searchPayload = {
      sort: {
        direction: 1,
        sortBy: 1
      },
      page: 0
    };
    this.isLoading = true;
    this.consignedWarehouseService.getProductsForWh(this.selectedWarehouse.value, this.quoteDetails.id, searchPayload, (productList) => {
      this.recordList = productList.consignedWarehouseItems;
      this.updateDisplayDataSource();
      this.isLoading = false;
      this.totalPages = Math.ceil(this.recordList.length / 10);
    });
  }

  updateQuantity(add, element: WarehouseProduct) {
    const elementPartNumber = this.quantityMap.get(element);
    this.hasItemsAdded = true;
    if (add) {
      if (!elementPartNumber) {
        this.quantityMap.set(element, 1);
      } else if (elementPartNumber < element.availableQuantity) {
        this.quantityMap.set(element, elementPartNumber + 1);
      }
    } else {
      if (elementPartNumber > 2) {
        this.quantityMap.set(element, elementPartNumber - 1);
      } else {
        this.quantityMap.delete(element);
      }
    }
  }

  changeInput(value, element) {
    this.hasItemsAdded = true;
    this.quantityMap.set(element, value);
    if (value > element.availableQuantity) {
      value = element.availableQuantity;
    }
    setTimeout(() => this.quantityMap.set(element, value));
  }

  handleClose(saveResults) {
    if (saveResults) {
      this.quoteToolsService.updateCWInProgress$.next(true);
      this.consignedWarehouseService.updateConsignedWarehouseValues(
        this.quoteDetails.id,
        this.selectedWarehouse,
        this.quantityMap,
        () => {
          this.quoteToolsService.hasProductsFromConsigned$.next(true);
          this.quoteToolsService.productListHasChanges$.next(true);
          this.quoteToolsService.refreshToolsList$.next(undefined);
          this.quoteToolsService.updateCWInProgress$.next(false);
        },
        () => {
          this.quoteToolsService.updateCWInProgress$.next(false);
        }
      );
    }
    this.dialogRef.close();
  }

  warehouseSwitchConfirmation(loadProducts) {
    if (loadProducts) {
      this.selectedWarehouse = this.warehouseList.find(w => w.value === this.warehouseForm.controls.selectedWarehouse.value);
      this.oldSelectedWarehouse = this.selectedWarehouse;
      this.updateProductList();
      this.hasItemsAdded = false;
      this.quantityMap.clear();
    } else {
      this.selectedWarehouse = this.oldSelectedWarehouse;
      this.warehouseForm.controls.selectedWarehouse.setValue(this.oldSelectedWarehouse.value);
    }
    this.showConfirmationOverlay = false;
    this.currentPage = 0;
  }

  selectNewWarehouse() {
    if (!this.hasItemsAdded) {
      this.warehouseSwitchConfirmation(true);
    } else {
      this.showConfirmationOverlay = true;
    }
  }

  /**
   * Loads a specific page
   * @param event - The page event
   */
  loadPage(event: PaginationPageEvent) {
    this.currentPage = event.pageNumber - 1;
    this.pageOffset = (event.pageNumber - 1) * this.pageSize;
    this.updateDisplayDataSource();
  }

  /**
   * Updates display data source to show only items for current page
   */
  private updateDisplayDataSource() {
    const startIndex = this.currentPage * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.displayDataSource = this.recordList.slice(startIndex, endIndex);
  }
}
